import React, { FC, useState } from 'react';
import { 
  Search, 
  Filter, 
  CheckCircle, 
  Info, 
  AlertTriangle, 
  ShieldAlert,
  Terminal,
  RefreshCw,
  ChevronDown
} from 'lucide-react';
import { SystemLog } from '../types';

interface LogsViewProps {
  logs: SystemLog[];
  onRefresh: () => void;
}

export const LogsView: FC<LogsViewProps> = ({ logs, onRefresh }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'info' | 'success' | 'warning' | 'error'>('all');

  // Calculate global logs count metrics by type in the current log database
  const allCount = logs.length;
  const infoCount = logs.filter(l => l.type === 'info').length;
  const successCount = logs.filter(l => l.type === 'success').length;
  const warningCount = logs.filter(l => l.type === 'warning').length;
  const errorCount = logs.filter(l => l.type === 'error').length;

  const filteredLogs = logs.filter(log => {
    const matchesSearch = 
      log.action.toLowerCase().includes(searchQuery.toLowerCase()) ||
      log.details.toLowerCase().includes(searchQuery.toLowerCase()) ||
      log.operator.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesType = typeFilter === 'all' || log.type === typeFilter;

    return matchesSearch && matchesType;
  });

  return (
    <div className="space-y-6 animate-fade-in">
      
      {/* Title */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="font-display font-medium text-2xl tracking-tight text-slate-900">
            Sistem Auditi və Təhlükəsizlik Jurnalları
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Bütün inzibati və əməliyyat addımlarının, abunəlik dəyişikliklərinin və süni intellekt fəaliyyətlərinin şəffaf auditi.
          </p>
        </div>

        <button
          type="button"
          onClick={onRefresh}
          className="px-3.5 py-1.5 bg-white border border-slate-200 hover:border-slate-300 text-slate-700 text-xs font-bold font-sans rounded-xl transition-all flex items-center gap-1.5 shadow-sm select-none cursor-pointer"
        >
          <RefreshCw className="w-3.5 h-3.5 text-slate-500 hover:rotate-180 transition-all duration-300" />
          <span>Jurnalları yenilə</span>
        </button>
      </div>

      {/* Controllers: Search & Filter Type */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs flex flex-col sm:flex-row gap-4 justify-between items-center">
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            id="logs-search-input"
            type="text"
            placeholder="Əməliyyat, operator və ya təfərrüat üzrə axtarış..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-1.5 text-xs border border-slate-200 rounded-xl focus:border-indigo-500 focus:outline-hidden text-slate-850 bg-slate-50/40"
          />
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto" id="log-grouping-container">
          <label htmlFor="log-type-select" className="text-xs font-bold text-slate-500 shrink-0 flex items-center gap-1.5 select-none">
            <Filter className="w-3.5 h-3.5 text-slate-400" />
            <span>Qrup:</span>
          </label>
          <div className="relative w-full sm:w-64">
            <select
              id="log-type-select"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as any)}
              className="w-full bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-800 rounded-xl pl-3 pr-9 py-2 text-xs font-bold outline-hidden focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 cursor-pointer transition-all appearance-none"
            >
              <option value="all">📋 CRM-dəki bütün əməliyyatlar ({allCount})</option>
              <option value="info">🔵 İnformasiya xarakterli qeydlər ({infoCount})</option>
              <option value="success">🟢 Uğurlu əməliyyatlar ({successCount})</option>
              <option value="warning">🟡 Xəbərdarlıq / Şübhəli fəaliyyət ({warningCount})</option>
              <option value="error">🔴 Kritik hadisələr ({errorCount})</option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2.5 text-slate-500 border-l border-slate-200/60 my-1.5">
              <ChevronDown className="h-3.5 w-3.5" />
            </div>
          </div>
        </div>
      </div>

      {/* Shell Log Display panel */}
      <div className="bg-slate-950 border border-slate-900 rounded-2xl overflow-hidden shadow-xl">
        {/* Terminal Header */}
        <div className="px-5 py-3.5 bg-slate-900/80 border-b border-slate-800/80 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Terminal className="w-4 h-4 text-emerald-400" />
            <span className="font-mono text-xs font-bold text-slate-300">
              super-admin@qr-menu-crm:~$ tail -n 50 operational.log
            </span>
          </div>

          <div className="flex gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-rose-500/60" />
            <span className="w-2.5 h-2.5 rounded-full bg-amber-500/60" />
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/60" />
          </div>
        </div>

        {/* Logs Listing Flow */}
        <div className="px-5 py-4 divide-y divide-slate-900/40 max-h-[500px] overflow-y-auto font-mono text-xs">
          {filteredLogs.length === 0 ? (
            <div className="py-12 text-center text-slate-500 italic">
              Seçilmiş axtarış meyarlarına görə heç bir jurnal qrafiki aşkar edilmədi.
            </div>
          ) : (
            filteredLogs.map((log) => {
              let TypeIcon = Info;
              let logColors = 'text-blue-400';
              let badgeBg = 'bg-blue-500/10 text-blue-400 border-blue-500/20';

              if (log.type === 'success') {
                TypeIcon = CheckCircle;
                logColors = 'text-emerald-400';
                badgeBg = 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
              } else if (log.type === 'warning') {
                TypeIcon = AlertTriangle;
                logColors = 'text-amber-400';
                badgeBg = 'bg-amber-500/10 text-amber-400 border-amber-500/20';
              } else if (log.type === 'error') {
                TypeIcon = ShieldAlert;
                logColors = 'text-rose-400';
                badgeBg = 'bg-rose-500/10 text-rose-400 border-rose-500/25';
              }

              return (
                <div 
                  key={log.id}
                  className="py-3 flex flex-col md:flex-row md:items-start gap-3 items-baseline group hover:bg-slate-900/20 px-2 rounded-lg transition-all"
                >
                  <span className="text-[10px] text-slate-500 w-44 shrink-0 select-none">
                    {new Date(log.timestamp).toLocaleString()}
                  </span>

                  <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[9px] font-bold uppercase rounded border ${badgeBg} shrink-0 select-none`}>
                    <TypeIcon className="w-3 h-3" />
                    <span>{log.type}</span>
                  </span>

                  <div className="flex-1 min-w-0">
                    <p className={`font-bold ${logColors} text-[11px] truncate`}>
                      {log.action}
                    </p>
                    <p className="text-[11px] text-slate-400 mt-1 leading-snug font-sans">
                      {log.details}
                    </p>
                  </div>

                  <div className="shrink-0 bg-slate-900/60 border border-slate-800 px-2 py-0.5 rounded text-[10px] text-slate-400 self-start md:self-auto uppercase tracking-wide font-mono">
                    {log.operator}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Console status summary */}
        <div className="p-4 bg-slate-900 border-t border-slate-800/80 flex items-center justify-between text-[11px] font-mono text-slate-500 select-none">
          <span>Keşdəki ümumi jurnalların sayı: {logs.length} ədəd.</span>
          <span>Filtrləmə: {typeFilter.toUpperCase()}</span>
        </div>
      </div>

    </div>
  );
};
